import Navbar2 from "@/component/navbar2";
import Footer from "@/components/BrandFooter";
import Dh from "@/components/Dh";
import { useState, useEffect, useRef, type ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { L7_MEDIA } from '@/data/landingMedia';
import { Star } from 'lucide-react';
import L7Reveal from '@/components/landing/L7Reveal';

/* ── palette (Corporate Services — orange) ─────────────────── */
const BRAND       = '#F08A24';
const BRAND_DEEP  = '#C76B12';
const BRAND_BRIGHT = '#F9A03F';
const BRAND_SOFT  = '#FDE8CF';
const INK         = '#17263A';
const INK2        = '#42505F';
const MUTED       = '#6E7888';
const Y           = '#FFD54F';
const YD          = '#E6B400';
const CREAM       = '#FCFDFF';
const PAPER       = '#F1F0E4';
const LINE        = '#E5E8EA';
const WHITE       = '#ffffff';
const GREEN       = '#15824E';

const SHADOW   = '0 26px 60px -24px rgba(23,38,58,.30)';
const SIGNUP = '/signup';

/* ── Types & default pricing model (admin-editable via Firestore) ── */
type NamePrice = { name: string; price: number | null; rec?: boolean; country?: string };
type Addon = { label: string; note: string; price: number | null; mainlandOnly: boolean; category?: string };
type Pkg = { name: string; tag: string; desc: string; inherit: string; features: string[]; priceYr: number; priceMo: number | null; discount: string | null; trial: string | null; govFees: boolean; popular: boolean };
type FeesModel = {
  rate: number;
  setupMainland: NamePrice[];
  setupFreezone: NamePrice[];
  echannel: number;
  visaResidence: number;
  addons: Addon[];
  packages: Pkg[];
};

const DEFAULT_FEES: FeesModel = {
  rate: 3.6725,
  setupMainland: [{ name: 'Dubai (DED)', price: 17850, rec: true, country: 'UAE' }],
  setupFreezone: [{ name: 'Meydan', price: null, rec: true }, { name: 'IFZA', price: null }, { name: 'DMCC', price: null }, { name: 'RAKEZ', price: null }, { name: 'SHAMS', price: null }, { name: 'Ajman', price: null }],
  echannel: 2625,
  visaResidence: 6090,
  addons: [
    { label: 'goAML Registration', note: 'UAE FIU portal · entity + compliance officer', price: 1000, mainlandOnly: false, category: 'Compliance' },
    { label: 'Employment visa', note: 'Entry permit + e-channel', price: 2625, mainlandOnly: false, category: 'Visas' },
    { label: 'Family visa', note: 'New / renewal', price: 5250, mainlandOnly: false, category: 'Visas' },
    { label: "Dependent / Maid's visa", note: 'Excl. deposit', price: 5000, mainlandOnly: false, category: 'Visas' },
    { label: 'Golden visa', note: '10-year · investor/talent', price: null, mainlandOnly: false, category: 'Visas' },
    { label: 'Green visa', note: 'Freelancer / skilled', price: null, mainlandOnly: false, category: 'Visas' },
    { label: 'Document attestation', note: 'MOFA / embassy / notary', price: 525, mainlandOnly: false, category: 'PRO & government' },
    { label: 'NOC & gov letters', note: 'Per NOC', price: 525, mainlandOnly: false, category: 'PRO & government' },
    { label: 'PO box & mail', note: 'Annual', price: 525, mainlandOnly: false, category: 'PRO & government' },
    { label: 'Payroll & WPS', note: 'Mainland only · monthly', price: null, mainlandOnly: true, category: 'PRO & government' },
    { label: 'POA attestation', note: 'Notary / MOFA', price: 368, mainlandOnly: false, category: 'Wills & POA' },
    { label: 'Expat will (DIFC)', note: 'Partner law firm', price: null, mainlandOnly: false, category: 'Wills & POA' },
  ],
  packages: [
    { name: 'Starter', tag: 'For New Business Setup', desc: 'For first-time founders who need the basics to launch with confidence.', inherit: 'Core features:', features: ['DED / Free Zone registration', 'Licence issuance', 'VAT registration'], priceYr: 1100, priceMo: null, discount: null, trial: 'Free Trial 30-Days', govFees: true, popular: false },
    { name: 'Total Compliance', tag: '', desc: 'For entrepreneurs who want to stay 100% compliant with UAE regulations.', inherit: 'Everything in Starter +', features: ['VAT registration + quarterly VAT filings', 'Accounting & bookkeeping dashboard (cash flow, P&L, balance sheet)', 'UAE Corporate Tax (CT) registration & annual return filing (FTA)', 'Includes up to 100 bookkeeping transactions per month', 'Annual financial statements', '1:1 Consultation with Certified Accountant'], priceYr: 14399, priceMo: 1499, discount: '20% OFF', trial: null, govFees: true, popular: false },
    { name: 'Business-in-a-Box™', tag: '', desc: 'For entrepreneurs who want full legal, accounting, and tax compliance, plus a dedicated accountant.', inherit: 'Everything in Total Compliance +', features: ['Includes up to 200 bookkeeping transactions per month', 'Quarterly compliance advisory session', 'Investor-ready reporting (deck financials, data room prep)', 'Priority support'], priceYr: 22999, priceMo: 2399, discount: '20% OFF', trial: null, govFees: true, popular: true },
  ],
};

const FAQS = [
  { q: "What's the difference between the plans and the custom setup?", a: 'The plans (Pulse, Total Compliance, Business-in-a-Box) are ongoing compliance subscriptions. The custom setup is a one-time builder for forming a company and adding visas & services à la carte.' },
  { q: 'Why do some items say "On request"?', a: 'A few items — free zone packages, Golden/Green visas and wills — vary by jurisdiction and case, so we confirm them on your free consultation rather than publishing a single number.' },
  { q: 'Are these prices final?', a: 'They reflect our current published fees and are a firm estimate. Government fees that change with your activity or visa quota are confirmed in your formal quote before you pay anything.' },
  { q: 'Can I see prices in USD?', a: 'Yes — toggle AED/USD anywhere on the page. USD is converted at the rate our team sets, shown for reference; invoicing is in AED.' },
  { q: 'What happens after I click Get started?', a: "You'll create an account with your selected package pre-filled, then move to a secure checkout. Nothing is charged until you confirm." },
];

const slug = (s: string) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/* ── FadeIn ─────────────────────────────────────────────── */
function FadeIn({ children, delay = 0, className = '', style }: { children: ReactNode; delay?: number; className?: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(es => {
      if (es[0]?.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={style}>
      {children}
    </div>
  );
}

const Eyebrow = ({ children }: { children: ReactNode }) => (
  <span style={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: INK, background: Y, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999 }}>
    {children}
  </span>
);

const Tick = ({ size = 17 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 3 }}>
    <path d="M5 13l4 4L19 7" stroke={GREEN} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PLAN_ICONS = [
  <><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" stroke={BRAND} strokeWidth="1.7" strokeLinejoin="round" /><circle cx="7.5" cy="7.5" r=".7" fill={BRAND} /></>,
  <><path d="M13 2L4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5z" stroke={BRAND} strokeWidth="1.7" strokeLinejoin="round" /></>,
  <><path d="M21 8l-9-5-9 5 9 5 9-5z" stroke={BRAND} strokeWidth="1.7" strokeLinejoin="round" /><path d="M3 8v8l9 5 9-5V8M12 13v8" stroke={BRAND} strokeWidth="1.7" strokeLinejoin="round" /></>,
];

/* ═══════════════════════════════════════════════════════════ */
export default function CorporatePricing() {
  const [fees, setFees] = useState<FeesModel>(DEFAULT_FEES);
  const [cur, setCur]   = useState<'AED' | 'USD'>('AED');
  const [bill, setBill] = useState<'mo' | 'yr'>('mo');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // estimator state
  const [type, setType]     = useState<'Mainland' | 'Freezone'>('Mainland');
  const [country, setCountry] = useState('UAE');
  const [jur, setJur]       = useState('Dubai (DED)');
  const [visas, setVisas]   = useState(0);
  const [add, setAdd]       = useState<Record<number, boolean>>({});
  const userPicked = useRef(false); // once true, we stop auto-selecting the recommended jurisdiction

  // Optional: live pricing from admin (defensive — no-op if the doc is absent)
  useEffect(() => {
    const ref = doc(db, 'corporateServices', 'current');
    return onSnapshot(ref, snap => {
      if (!snap.exists()) return;
      const d = snap.data() as Partial<FeesModel>;
      setFees(prev => ({
        rate: typeof d.rate === 'number' ? d.rate : prev.rate,
        setupMainland: Array.isArray(d.setupMainland) ? d.setupMainland : prev.setupMainland,
        setupFreezone: Array.isArray(d.setupFreezone) ? d.setupFreezone : prev.setupFreezone,
        echannel: typeof d.echannel === 'number' ? d.echannel : prev.echannel,
        visaResidence: typeof d.visaResidence === 'number' ? d.visaResidence : prev.visaResidence,
        addons: Array.isArray(d.addons) ? d.addons : prev.addons,
        packages: Array.isArray(d.packages) ? d.packages : prev.packages,
      }));
    }, err => { if (err.name !== 'AbortError') console.warn('corporate pricing snapshot:', err); });
  }, []);

  const mlCountries = () => [...new Set((fees.setupMainland || []).map(x => x.country || 'UAE'))];
  const curList = () => (type === 'Mainland' ? (fees.setupMainland || []).filter(x => (x.country || 'UAE') === country) : fees.setupFreezone);
  const jurList = () => curList().map(x => x.name);
  const recName = (list: NamePrice[]) => (list.find(x => x.rec)?.name) ?? (list[0]?.name ?? '');

  // Pre-select the recommended jurisdiction (until the visitor picks their own),
  // keep the country/jurisdiction valid, and clear mainland-only add-ons under free zone.
  useEffect(() => {
    const rec = fees.setupMainland.find(x => x.rec) || fees.setupMainland[0];
    const cs = mlCountries();
    let ctry = country;
    if (type === 'Mainland') {
      if (!userPicked.current && rec?.country) ctry = rec.country;
      else if (!cs.includes(country)) ctry = cs[0] ?? 'UAE';
      if (ctry !== country) setCountry(ctry);
    }
    const list = type === 'Mainland'
      ? fees.setupMainland.filter(x => (x.country || 'UAE') === ctry)
      : fees.setupFreezone;
    const want = (!userPicked.current || !list.some(x => x.name === jur)) ? recName(list) : jur;
    if (want !== jur) setJur(want);
    if (type !== 'Mainland') {
      setAdd(prev => {
        const next = { ...prev };
        fees.addons.forEach((a, i) => { if (a.mainlandOnly) next[i] = false; });
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, country, fees]);
  const recdName = recName(curList());

  const setupItem = () => curList().find(x => x.name === jur);
  const setupPrice = () => setupItem()?.price ?? null;

  // currency
  const conv = (a: number) => (cur === 'USD' ? a / fees.rate : a);
  const money = (a: number) => (cur === 'USD' ? `$${Math.round(a / fees.rate).toLocaleString('en-US')}` : `AED ${Math.round(a).toLocaleString('en-US')}`);
  const Money = ({ aed }: { aed: number }) => {
    const val = Math.round(conv(aed)).toLocaleString('en-US');
    return cur === 'USD' ? <>${val}</> : <><Dh />{val}</>;
  };

  const activeAddons = fees.addons.map((a, i) => ({ a, i })).filter(o => !(o.a.mainlandOnly && type !== 'Mainland'));

  // estimate
  type Line = { label: string; aed: number | null };
  const lines: Line[] = [];
  let total = 0; let hasReq = false;
  const sp = setupPrice();
  if (sp == null) { lines.push({ label: `${type} — ${jur}`, aed: null }); hasReq = true; }
  else { lines.push({ label: `${type} — ${jur}`, aed: sp }); total += sp; }
  if (visas > 0) {
    lines.push({ label: 'E-channel registration', aed: fees.echannel }); total += fees.echannel;
    lines.push({ label: `Residence visa × ${visas}`, aed: fees.visaResidence * visas }); total += fees.visaResidence * visas;
  }
  fees.addons.forEach((a, i) => {
    if (a.mainlandOnly && type !== 'Mainland') return;
    if (!add[i]) return;
    if (a.price == null) { lines.push({ label: a.label, aed: null }); hasReq = true; }
    else { lines.push({ label: a.label, aed: a.price }); total += a.price; }
  });
  const anyMonthly = fees.addons.some((a, i) => add[i] && /payroll|wps/i.test(a.label));

  const addCodes = Object.keys(add).filter(k => add[+k]).join(',');
  const estimateHref = `${SIGNUP}?service=corporate&setup=${type}&jur=${slug(jur)}&visas=${visas}&add=${addCodes}&cur=${cur}`;
  const planHref = (name: string) => `${SIGNUP}?service=corporate&plan=${slug(name)}&billing=${bill}&cur=${cur}`;

  const scrollToId = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const primaryBtn = { display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, border: `2px solid ${BRAND_DEEP}`, cursor: 'pointer', background: BRAND, color: WHITE, textDecoration: 'none' } as const;
  const ghostBtn   = { display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, border: `2px solid ${INK}`, cursor: 'pointer', background: 'transparent', color: INK, textDecoration: 'none' } as const;
  const yellowBtn  = { display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, border: `2px solid ${YD}`, cursor: 'pointer', background: Y, color: INK, textDecoration: 'none' } as const;

  return (
    <div style={{ background: CREAM, color: INK, fontFamily: "'Montserrat', system-ui, sans-serif", lineHeight: 1.55, overflowX: 'clip' }}>
      <style>{`
        .cs-btn{transition:transform .16s, box-shadow .22s, background .2s, color .2s;}
        .cs-btn:hover{transform:translateY(-3px);}
        .cs-ghost:hover{background:${INK};color:${CREAM} !important;}
        .cs-cur{border:none;background:${WHITE};font-family:inherit;font-weight:800;font-size:13px;padding:9px 16px;cursor:pointer;color:${MUTED};transition:background .2s,color .2s;}
        .cs-cur.on{background:${INK};color:#fff;}
        .cs-seg{border:1.5px solid ${LINE};background:#fff;font-family:inherit;font-weight:700;font-size:12.5px;padding:9px 14px;border-radius:9px;cursor:pointer;color:${INK2};transition:all .15s;}
        .cs-seg.on{background:${INK};color:#fff;border-color:${INK};}
        .cs-seg:hover{border-color:${BRAND};}
        .cs-tier{transition:transform .25s cubic-bezier(.2,.7,.2,1), box-shadow .25s;}
        .cs-tier:hover{transform:translateY(-6px);box-shadow:${SHADOW};}
        .cs-addon{transition:.15s;}
        .cs-addon:hover{border-color:${BRAND_BRIGHT};box-shadow:0 8px 24px -14px rgba(23,38,58,.22);}
        .cs-step button{width:42px;height:42px;border:none;background:#fff;font-family:inherit;font-weight:800;font-size:17px;cursor:pointer;color:${INK};}
        .cs-step button:hover{background:${BRAND_SOFT};}
        @media(max-width:880px){.cs-est{grid-template-columns:1fr !important;}.cs-addons{grid-template-columns:1fr !important;}}
      `}</style>

      <div className="sticky top-0 z-50 w-full">
        <Navbar2 bgColor="white" logoTo="/" signupTo="/signup" />
      </div>

      {/* ── HERO ──────────────────────────────────────────── */}
      <section style={{ position: 'relative', background: `radial-gradient(120% 90% at 82% -10%, ${BRAND_SOFT} 0%, ${CREAM} 55%)`, overflow: 'hidden', padding: '74px 0 28px' }}>
        <div className="max-w-[820px] mx-auto px-6" style={{ position: 'relative', zIndex: 2 }}>
          <FadeIn><Eyebrow>Company Setup · Visas · PRO — UAE</Eyebrow></FadeIn>
          <FadeIn delay={80}>
            <h1 style={{ fontSize: 'clamp(40px,5.4vw,66px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.04, marginTop: 18 }}>
              Set up in the UAE. Know your cost in{' '}
              <span style={{ position: 'relative', whiteSpace: 'nowrap' }}>
                <span aria-hidden style={{ position: 'absolute', left: -2, right: -2, bottom: 6, height: '0.32em', background: Y, zIndex: -1, transform: 'skewX(-4deg)', display: 'block' }} />
                60 seconds.
              </span>
            </h1>
          </FadeIn>
          <FadeIn delay={160}>
            <p style={{ color: INK2, fontSize: 20, marginTop: 24, maxWidth: 640 }}>
              Choose a plan to stay compliant and grow, or build a custom company setup with the exact visas and services you need — priced instantly in AED and USD.
            </p>
          </FadeIn>
          <FadeIn delay={240}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 28 }}>
              <a href="#packages" onClick={e => scrollToId(e, 'packages')} className="cs-btn" style={yellowBtn}>See plans <span aria-hidden>→</span></a>
              <a href="#estimator" onClick={e => scrollToId(e, 'estimator')} className="cs-btn cs-ghost" style={ghostBtn}>Build a custom setup</a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── UAE band (real photography) ─────────────────────── */}
      <div className="l7">
        <L7Reveal />
        <section className="l7-sec l7-sec--tight">
          <div className="l7-wrap">
            <div className="l7-figure" style={{ position: 'relative', aspectRatio: '21 / 7' }}>
              <img src={L7_MEDIA.dubaiArchitecture} alt="Dubai's business district" loading="lazy" />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(18,47,82,.82) 0%, rgba(18,47,82,.45) 48%, transparent 78%)' }} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                <div style={{ padding: 'clamp(20px,4vw,48px)', maxWidth: 560, color: '#fff' }}>
                  <h3 style={{ fontSize: 'clamp(19px,2.4vw,27px)', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                    Mainland or free zone — one published price.
                  </h3>
                  <p style={{ marginTop: 10, fontSize: 15, color: '#D8E1EE' }}>
                    Formation, licensing and visas across every major UAE jurisdiction, quoted before you commit to anything.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ── PACKAGES ──────────────────────────────────────── */}
      <section id="packages" style={{ padding: '60px 0 92px' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center max-w-[760px] mx-auto">
            <Eyebrow>Plans</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.2vw,48px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '16px 0 12px' }}>Choose your plan</h2>
            <p style={{ color: INK2, fontSize: 19, maxWidth: 620, margin: '0 auto' }}>Three plans to keep you compliant and growing. Prices in <b>{cur}</b>.</p>
            <div style={{ marginTop: 18, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', border: `1.5px solid ${LINE}`, borderRadius: 999, padding: 4, background: WHITE }}>
                <button onClick={() => setBill('mo')} style={{ border: 'none', background: bill === 'mo' ? INK : 'transparent', color: bill === 'mo' ? '#fff' : MUTED, fontFamily: 'inherit', fontWeight: 800, fontSize: 13, padding: '8px 18px', borderRadius: 999, cursor: 'pointer' }}>Monthly</button>
                <button onClick={() => setBill('yr')} style={{ border: 'none', background: bill === 'yr' ? INK : 'transparent', color: bill === 'yr' ? '#fff' : MUTED, fontFamily: 'inherit', fontWeight: 800, fontSize: 13, padding: '8px 18px', borderRadius: 999, cursor: 'pointer' }}>Annual <span style={{ color: bill === 'yr' ? Y : GREEN, fontSize: 11, marginLeft: 5 }}>save 20%</span></button>
              </span>
              <span style={{ display: 'inline-flex', border: `1.5px solid ${LINE}`, borderRadius: 10, overflow: 'hidden' }}>
                <button className={`cs-cur ${cur === 'AED' ? 'on' : ''}`} onClick={() => setCur('AED')}>AED</button>
                <button className={`cs-cur ${cur === 'USD' ? 'on' : ''}`} onClick={() => setCur('USD')}>USD</button>
              </span>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-[18px] mt-11 items-stretch">
            {fees.packages.map((p, i) => (
              <FadeIn key={p.name + i} delay={i * 100} className="h-full">
                <div className="cs-tier h-full" style={{ background: WHITE, border: `1.5px solid ${p.popular ? BRAND : LINE}`, borderRadius: 20, padding: '28px 26px', display: 'flex', flexDirection: 'column', boxShadow: p.popular ? SHADOW : undefined, position: 'relative' }}>
                  {p.popular && <span style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: BRAND, color: '#fff', border: `2px solid ${BRAND_DEEP}`, fontSize: 12, fontWeight: 800, padding: '4px 14px', borderRadius: 999, whiteSpace: 'nowrap' }}>Most popular</span>}
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: p.popular ? BRAND_SOFT : PAPER, display: 'grid', placeItems: 'center', marginBottom: 12 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">{PLAN_ICONS[i % PLAN_ICONS.length]}</svg>
                  </div>
                  {p.tag && <span style={{ fontSize: 12, fontWeight: 700, color: MUTED }}>{p.tag}</span>}
                  <h3 style={{ fontSize: 21, fontWeight: 800, margin: '2px 0 4px' }}>{p.name}</h3>
                  <p style={{ fontSize: 13, color: MUTED, minHeight: 54, lineHeight: 1.5 }}>{p.desc}</p>
                  <div style={{ fontWeight: 800, fontSize: 13, margin: '14px 0 8px', color: INK }}>{p.inherit}</div>
                  <ul style={{ listStyle: 'none', margin: '0 0 6px', display: 'flex', flexDirection: 'column', gap: 9, flex: 1 }}>
                    {p.features.map((f, j) => (
                      <li key={j} style={{ display: 'flex', gap: 9, fontSize: 14, alignItems: 'flex-start', color: INK2 }}><Tick />{f}</li>
                    ))}
                  </ul>
                  <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: `1px solid ${LINE}` }}>
                    {p.trial && <div style={{ color: BRAND_DEEP, fontWeight: 800, fontSize: 12.5, marginBottom: 8 }}>{p.trial}</div>}
                    {(() => {
                      const annualOnly = p.priceMo == null;
                      const big = bill === 'mo' && !annualOnly ? p.priceMo! : p.priceYr;
                      const unit = bill === 'mo' && !annualOnly ? ' / month' : ' / yr';
                      const showOff = bill === 'yr' && !annualOnly && p.discount;
                      return (
                        <>
                          <div style={{ fontSize: 26, fontWeight: 800, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 3 }}>
                            <Money aed={big} /><small style={{ fontSize: 13, color: MUTED, fontWeight: 600 }}>{unit}</small>
                            {showOff && <span style={{ color: GREEN, fontWeight: 800, fontSize: 12, marginLeft: 6 }}>{p.discount}</span>}
                          </div>
                          {!annualOnly && bill === 'yr' && <div style={{ fontSize: 12.5, color: INK2, fontWeight: 700, marginTop: 2 }}>Works out at <Money aed={Math.round(p.priceYr / 12)} />/mo</div>}
                          {!annualOnly && bill === 'mo' && <div style={{ fontSize: 12.5, color: INK2, fontWeight: 700, marginTop: 2 }}><Money aed={p.priceMo! * 12} /> a year</div>}
                          {annualOnly && <div style={{ fontSize: 12.5, color: INK2, fontWeight: 700, marginTop: 2 }}>Annual plan only</div>}
                          {p.govFees && <div style={{ fontSize: 11.5, color: MUTED, marginTop: 3 }}>(+ government fees)</div>}
                        </>
                      );
                    })()}
                    <a href={planHref(p.name)} className="cs-btn" style={{ ...(p.popular ? primaryBtn : ghostBtn), display: 'flex', justifyContent: 'center', width: '100%', marginTop: 14, padding: '12px 18px', fontSize: 14.5 }}>Get started</a>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: 22, color: MUTED, fontSize: 14 }}>
            Need just setup &amp; visas? <a href="#estimator" onClick={e => scrollToId(e, 'estimator')} style={{ color: BRAND, fontWeight: 700 }}>Build a custom package below →</a>
          </p>
        </div>
      </section>

      {/* ── ESTIMATOR ─────────────────────────────────────── */}
      <section id="estimator" style={{ background: PAPER, padding: '72px 0 92px' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center max-w-[760px] mx-auto">
            <Eyebrow>À la carte</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.2vw,48px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '16px 0 12px' }}>Or build a custom setup</h2>
            <p style={{ color: INK2, fontSize: 19, maxWidth: 620, margin: '0 auto' }}>Need just a company setup, jurisdiction and visas? Price it live. Prices in <b>{cur}</b>.</p>
          </FadeIn>

          <FadeIn style={{ marginTop: 26 }}>
            <div className="cs-est" style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.85fr', background: WHITE, border: `1px solid ${LINE}`, borderRadius: 24, boxShadow: SHADOW, overflow: 'hidden' }}>
              {/* left */}
              <div style={{ padding: '30px 32px' }}>
                {/* 1 setup type */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 800, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>1 · Setup type</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                    {(['Mainland', 'Freezone'] as const).map(v => (
                      <button key={v} className={`cs-seg ${type === v ? 'on' : ''}`} onClick={() => setType(v)}>{v === 'Freezone' ? 'Free zone' : v}</button>
                    ))}
                  </div>
                </div>
                {/* 2 jurisdiction */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 800, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>2 · {type === 'Mainland' ? 'Country & jurisdiction' : 'Jurisdiction'}</label>
                  {type === 'Mainland' && mlCountries().length > 0 && (
                    <select value={country} onChange={e => { userPicked.current = true; setCountry(e.target.value); }}
                      style={{ width: '100%', border: `1.5px solid ${LINE}`, borderRadius: 12, padding: '13px 14px', fontSize: 15, fontWeight: 700, color: INK, fontFamily: 'inherit', background: WHITE, cursor: 'pointer', marginBottom: 10 }}>
                      {mlCountries().map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                  <select value={jur} onChange={e => { userPicked.current = true; setJur(e.target.value); }}
                    style={{ width: '100%', border: `1.5px solid ${LINE}`, borderRadius: 12, padding: '13px 14px', fontSize: 15, fontWeight: 700, color: INK, fontFamily: 'inherit', background: WHITE, cursor: 'pointer' }}>
                    {curList().map(x => <option key={x.name} value={x.name}>{x.name}{x.rec ? ' (recommended)' : ''}</option>)}
                  </select>
                  {jur && jur === recdName && (
                    <div style={{ marginTop: 8 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: BRAND_SOFT, color: BRAND_DEEP, fontSize: 11, fontWeight: 800, padding: '5px 10px', borderRadius: 999 }}><Star size={12} fill="currentColor" strokeWidth={0} /> Recommended — pre-selected for new clients</span>
                    </div>
                  )}
                </div>
                {/* 3 visas */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 800, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>3 · Residence visas</label>
                  <span className="cs-step" style={{ display: 'inline-flex', alignItems: 'center', border: `1.5px solid ${LINE}`, borderRadius: 11, overflow: 'hidden', verticalAlign: 'middle' }}>
                    <button onClick={() => setVisas(v => Math.max(0, v - 1))}>−</button>
                    <span style={{ minWidth: 54, textAlign: 'center', fontWeight: 800, fontSize: 16 }}>{visas}</span>
                    <button onClick={() => setVisas(v => Math.min(50, v + 1))}>+</button>
                  </span>
                  <span style={{ fontSize: 11.5, color: MUTED, fontWeight: 600, marginLeft: 12 }}>{money(fees.visaResidence)} each</span>
                </div>
                {/* 4 addons */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 800, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>4 · Add services</label>
                  <div className="cs-addons" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
                    {activeAddons.map(({ a, i }) => {
                      const on = !!add[i];
                      return (
                        <div key={i} className="cs-addon" onClick={() => setAdd(prev => ({ ...prev, [i]: !prev[i] }))}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, border: `1.5px solid ${on ? BRAND : LINE}`, borderRadius: 11, padding: '11px 13px', cursor: 'pointer', background: on ? BRAND_SOFT : WHITE }}>
                          <span style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${on ? BRAND : LINE}`, flex: 'none', display: 'grid', placeItems: 'center', background: on ? BRAND : WHITE }}>
                            {on && <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}><path d="M5 13l4 4L19 7" /></svg>}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                            {a.label}
                            <small style={{ display: 'block', color: MUTED, fontWeight: 600, fontSize: 11.5 }}>{a.price == null ? 'On request' : money(a.price)}</small>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* currency */}
                <div>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 800, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Currency</label>
                  <span style={{ display: 'inline-flex', border: `1.5px solid ${LINE}`, borderRadius: 10, overflow: 'hidden' }}>
                    <button className={`cs-cur ${cur === 'AED' ? 'on' : ''}`} onClick={() => setCur('AED')}>AED</button>
                    <button className={`cs-cur ${cur === 'USD' ? 'on' : ''}`} onClick={() => setCur('USD')}>USD</button>
                  </span>
                </div>
              </div>

              {/* right */}
              <div style={{ background: 'linear-gradient(165deg,#10243b,#17263A)', color: '#fff', padding: '30px 32px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9fb0c9', fontWeight: 800 }}>Your estimate</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', margin: '4px 0 14px', lineHeight: 1.3 }}>{type} — {jur}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 40, fontWeight: 900, color: '#fff', letterSpacing: '-0.03em' }}><Money aed={total} /></span>
                  <span style={{ fontSize: 13, color: '#9fb0c9', fontWeight: 700 }}>one-time{anyMonthly ? ' + monthly' : ''}</span>
                </div>
                <div style={{ color: '#AEB9D6', fontSize: 12.5, fontWeight: 600, marginTop: 5 }}>{lines.length} item{lines.length > 1 ? 's' : ''} selected</div>

                <div style={{ margin: '16px 0', borderTop: '1px solid #233056', paddingTop: 12, maxHeight: 230, overflow: 'auto' }}>
                  {lines.map((l, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12.5, color: '#cdd8ea', padding: '5px 0' }}>
                      <span>{l.label}</span>
                      <b style={{ color: '#fff', whiteSpace: 'nowrap' }}>{l.aed == null ? <span style={{ color: '#FFE38A' }}>On request</span> : <Money aed={l.aed} />}</b>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #233056', marginTop: 'auto', paddingTop: 12, fontSize: 14, color: '#fff', fontWeight: 800 }}>
                  <span>Total today</span>
                  <span><Money aed={total} />{hasReq && <span style={{ color: '#FFE38A', fontWeight: 700 }}> + req.</span>}</span>
                </div>
                <a href={estimateHref} className="cs-btn" style={{ ...yellowBtn, width: '100%', justifyContent: 'center', marginTop: 16 }}>Get started <span aria-hidden>→</span></a>
                {hasReq && <div style={{ fontSize: 11, color: '#8fa0bb', marginTop: 9, textAlign: 'center' }}>Some items priced on your free call — total shown excludes those.</div>}
              </div>
            </div>
          </FadeIn>
          <p style={{ textAlign: 'center', marginTop: 16, color: MUTED, fontSize: 13 }}>
            Some items show <b>On request</b> until pricing is confirmed — your advisor finalises these on your free call.
          </p>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────── */}
      <section id="faq" style={{ padding: '80px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center">
            <Eyebrow>Pricing FAQ</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.2vw,48px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '16px 0 0' }}>Questions about pricing</h2>
          </FadeIn>
          <FadeIn>
            <div style={{ maxWidth: 800, margin: '30px auto 0' }}>
              {FAQS.map((faq, i) => (
                <div key={i} style={{ borderBottom: `1px solid ${LINE}` }}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', padding: '20px 4px', fontSize: 17, fontFamily: 'inherit', fontWeight: 700, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, color: INK }}>
                    {faq.q}
                    <span style={{ fontSize: 24, transition: 'transform .3s', color: BRAND_DEEP, transform: openFaq === i ? 'rotate(45deg)' : 'none', flexShrink: 0 }}>+</span>
                  </button>
                  <div style={{ maxHeight: openFaq === i ? 260 : 0, overflow: 'hidden', transition: 'max-height .32s ease', color: INK2, fontSize: 15 }}>
                    <p style={{ padding: '0 4px 20px' }}>{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────── */}
      <section style={{ padding: '0 0 104px' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn>
            <div style={{ background: INK, borderRadius: 30, padding: '64px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <div aria-hidden style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(${Y} .9px, transparent .9px)`, backgroundSize: '24px 24px', opacity: 0.10, pointerEvents: 'none' }} />
              <h2 style={{ fontSize: 'clamp(32px,4.6vw,52px)', fontWeight: 800, letterSpacing: '-0.025em', color: WHITE, position: 'relative' }}>
                Get your UAE setup moving.
              </h2>
              <p style={{ fontSize: 18, color: 'rgba(255,255,255,.9)', maxWidth: 560, margin: '14px auto 26px', position: 'relative' }}>
                Pick a plan or build a custom package, sign up in minutes, and hand off formation, visas and PRO to one team.
              </p>
              <a href="#packages" onClick={e => scrollToId(e, 'packages')} className="cs-btn" style={{ ...yellowBtn, position: 'relative' }}>See plans <span aria-hidden>→</span></a>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
